// components/calendar/GoogleEventDetailPanel.js
// Pannello dettaglio read-only per eventi Google Calendar esterni

import React from 'react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { 
  X, ChevronLeft, Calendar, Clock, MapPin, FileText, 
  ExternalLink, AlertCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';

const GoogleEventDetailPanel = ({
  event,
  onClose,
}) => {
  if (!event) return null;

  const startDate = event.start ? new Date(event.start) : null;
  const endDate = event.end ? new Date(event.end) : null;
  const duration = event.duration_minutes || 60;
  const durationHours = Math.floor(duration / 60);
  const durationMins = duration % 60;
  const durationText = durationHours > 0 
    ? `${durationHours}h${durationMins > 0 ? durationMins.toString().padStart(2, '0') : '00'}`
    : `${durationMins} min`;

  // Google Maps link for location
  const mapsUrl = event.location 
    ? `https://maps.google.com/?q=${encodeURIComponent(event.location)}`
    : null;

  return (
    <div className="fixed inset-y-0 right-0 w-full max-w-lg bg-white shadow-xl z-50 flex flex-col border-l border-[#DCDCDC]">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-[#DCDCDC] bg-[#FAFAFA]">
        <button 
          onClick={onClose}
          className="flex items-center gap-1 text-[#0066B3] hover:text-[#004C8C] text-sm font-medium"
        >
          <ChevronLeft className="w-4 h-4" />
          Torna al calendario
        </button>
        <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
          <X className="w-5 h-5 text-[#737373]" />
        </button>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4">
          {/* Google Event Badge */}
          <div className="flex items-center gap-2 p-3 bg-[#DB4437]/10 rounded-lg border border-[#DB4437]/20">
            <div className="w-8 h-8 flex items-center justify-center">
              <svg viewBox="0 0 24 24" className="w-6 h-6">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
            </div>
            <div>
              <span className="text-sm font-medium text-[#DB4437]">Evento Google Calendar</span>
              <p className="text-xs text-[#737373]">Questo evento è importato dal tuo Google Calendar</p>
            </div>
          </div>

          {/* Info Evento Card */}
          <div className="bg-white border border-[#DCDCDC] rounded-lg overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-[#DCDCDC]">
              <h3 className="font-semibold text-[#3C3C3C]">Dettagli evento</h3>
              {event.htmlLink && (
                <a
                  href={event.htmlLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-sm text-[#0066B3] hover:underline"
                >
                  Apri in Google
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>

            <div className="p-4">
              {/* Titolo */}
              <div className="flex items-start gap-2 mb-4">
                <div className="w-1 h-6 rounded-full bg-[#DB4437]" />
                <h4 className="text-lg font-medium text-[#3C3C3C]">{event.title || 'Evento senza titolo'}</h4>
              </div>

              {/* Grid info */}
              <div className="space-y-4 text-sm">
                {/* Data e ora */}
                {startDate && (
                  <div className="flex items-start gap-3">
                    <Calendar className="w-4 h-4 mt-0.5 text-[#737373]" />
                    <div>
                      <span className="text-[#737373] block text-xs mb-0.5">Data e ora</span>
                      <span className="text-[#3C3C3C] font-medium">
                        {format(startDate, "EEEE dd MMMM yyyy", { locale: it })}
                      </span>
                      <div className="text-[#3C3C3C]">
                        {format(startDate, "HH:mm", { locale: it })}
                        {endDate && ` - ${format(endDate, "HH:mm", { locale: it })}`}
                      </div>
                    </div>
                  </div>
                )}

                {/* Durata */}
                <div className="flex items-start gap-3">
                  <Clock className="w-4 h-4 mt-0.5 text-[#737373]" />
                  <div>
                    <span className="text-[#737373] block text-xs mb-0.5">Durata</span>
                    <span className="text-[#3C3C3C]">{durationText}</span>
                  </div>
                </div>

                {/* Località */}
                {event.location && (
                  <div className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 mt-0.5 text-[#737373]" />
                    <div>
                      <span className="text-[#737373] block text-xs mb-0.5">Località</span>
                      {mapsUrl ? (
                        <a 
                          href={mapsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#0066B3] hover:underline flex items-start gap-1"
                        >
                          <span>{event.location}</span>
                          <ExternalLink className="w-3 h-3 mt-0.5 flex-shrink-0" />
                        </a>
                      ) : (
                        <span className="text-[#3C3C3C]">{event.location}</span>
                      )}
                    </div>
                  </div>
                )}

                {/* Descrizione */}
                {event.notes && (
                  <div className="flex items-start gap-3">
                    <FileText className="w-4 h-4 mt-0.5 text-[#737373]" />
                    <div>
                      <span className="text-[#737373] block text-xs mb-0.5">Descrizione</span>
                      <p className="text-[#3C3C3C] whitespace-pre-wrap">{event.notes}</p>
                    </div>
                  </div>
                )}

                {/* Status */}
                {event.status && (
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-4 h-4 mt-0.5 text-[#737373]" />
                    <div>
                      <span className="text-[#737373] block text-xs mb-0.5">Stato</span>
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                        event.status === 'annullato' ? 'bg-red-100 text-red-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {event.status === 'annullato' ? 'Annullato' : 'Confermato'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Note informativa */}
          <div className="p-3 bg-[#F5F5F5] rounded-lg text-sm text-[#737373]">
            <p className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>
                Questo evento è in sola lettura. Per modificarlo, aprilo in Google Calendar.
              </span>
            </p>
          </div>
        </div>
      </ScrollArea>

      {/* Footer con azioni */}
      <div className="p-4 border-t border-[#DCDCDC] bg-[#FAFAFA] space-y-2">
        {event.htmlLink && (
          <a
            href={event.htmlLink}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full py-2 px-4 bg-[#DB4437] text-white rounded-md hover:bg-[#C33326] transition-colors"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            </svg>
            Apri in Google Calendar
          </a>
        )}
        <Button 
          variant="outline" 
          className="w-full"
          onClick={onClose}
        >
          Chiudi
        </Button>
      </div>
    </div>
  );
};

export default GoogleEventDetailPanel;
